import React from 'react';
import { Box, Stack, Typography, IconButton, Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import {
  ChartContainer, ChartsReferenceLine, ChartsXAxis, ChartsYAxis, LinePlot,
} from '@mui/x-charts';
import dayjs from 'dayjs';
import { useConfig } from '../../components/ConfigContext';

const cyan = 'var(--v4-cyan)';

const cardSx = {
  background: 'rgba(var(--v4-surface-rgb),0.88)',
  border: '1px solid rgba(var(--v4-cyan-rgb),0.12)',
  borderRadius: '18px',
  backdropFilter: 'blur(20px)',
  p: 3,
};

const labelSx = {
  fontSize: '0.58rem',
  fontWeight: 800,
  textTransform: 'uppercase',
  letterSpacing: '0.15em',
  color: 'rgba(var(--v4-cyan-rgb),0.5)',
  mb: 0.5,
  display: 'block',
};

const descSx = {
  fontSize: '0.7rem',
  color: 'rgba(var(--v4-muted-rgb),0.4)',
  mb: 1,
  display: 'block',
};

const stepBtnSx = {
  width: 32,
  height: 32,
  border: '1px solid rgba(var(--v4-cyan-rgb),0.28)',
  borderRadius: '50%',
  color: cyan,
  background: 'rgba(var(--v4-cyan-rgb),0.05)',
  p: 0,
  '&:hover': { background: 'rgba(var(--v4-cyan-rgb),0.14)', borderColor: cyan },
};

const numInputStyle = {
  width: '5rem',
  textAlign: 'center',
  background: 'rgba(var(--v4-cyan-rgb),0.04)',
  border: '1px solid rgba(var(--v4-cyan-rgb),0.14)',
  borderRadius: 8,
  color: 'rgba(var(--v4-text-rgb),0.92)',
  fontSize: '1rem',
  fontWeight: 700,
  fontFamily: 'Inter, Roboto, sans-serif',
  padding: '7px 10px',
  outline: 'none',
};

function V4Stepper({ value, onChange, min = 0, max = 30, step = 1 }) {
  const dec = () => onChange(Math.max(min, (value ?? 0) - step));
  const inc = () => onChange(Math.min(max, (value ?? 0) + step));
  const handleChange = e => {
    const v = parseFloat(e.target.value);
    if (!isNaN(v)) onChange(Math.max(min, Math.min(max, v)));
  };
  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <IconButton onClick={dec} sx={stepBtnSx} size="small"><RemoveIcon sx={{ fontSize: 16 }} /></IconButton>
      <input value={value ?? ''} onChange={handleChange} style={numInputStyle} />
      <IconButton onClick={inc} sx={stepBtnSx} size="small"><AddIcon sx={{ fontSize: 16 }} /></IconButton>
    </Stack>
  );
}

function ConfigBlock({ label, description, value, onChange, min = 0, max = 30, step = 1 }) {
  return (
    <Box sx={{
      background: 'rgba(var(--v4-cyan-rgb),0.03)',
      border: '1px solid rgba(var(--v4-cyan-rgb),0.08)',
      borderRadius: '12px',
      p: 2,
    }}>
      <Typography component="span" sx={labelSx}>{label}</Typography>
      <Typography component="span" sx={descSx}>{description}</Typography>
      <V4Stepper value={value} onChange={onChange} min={min} max={max} step={step} />
    </Box>
  );
}

export default function V4ConfigTemperatur({ is_managed }) {
  const { state, dispatch, saveConfig } = useConfig();

  return (
    <Box sx={cardSx}>
      <Stack direction="row" flexWrap="wrap" gap={2} useFlexGap>
        <ConfigBlock
          label="Temperatur Tag"
          description="ab Sonnenaufgang"
          value={state.config.temperatur_tag}
          onChange={v => dispatch({ type: 'SET_FIELD', field: 'temperatur_tag', value: v })}
          min={0} max={40}
        />
        <ConfigBlock
          label="Temperatur Nacht"
          description="ab Sonnenuntergang"
          value={state.config.temperatur_nacht}
          onChange={v => dispatch({ type: 'SET_FIELD', field: 'temperatur_nacht', value: v })}
          min={15} max={35}
        />
        <ConfigBlock
          label="Abweichung Tag"
          description="Toleranz in °C"
          value={state.config.abweichung_temperatur}
          onChange={v => dispatch({ type: 'SET_FIELD', field: 'abweichung_temperatur', value: v })}
          step={0.5} max={10}
        />
        <ConfigBlock
          label="Abweichung Nacht"
          description="Toleranz in °C"
          value={state.config.abweichung_temperatur}
          onChange={v => dispatch({ type: 'SET_FIELD', field: 'abweichung_temperatur', value: v })}
          step={0.5} max={10}
        />
      </Stack>

      <Box sx={{ mt: 3, borderRadius: '12px', overflow: 'hidden', border: '1px solid rgba(var(--v4-cyan-rgb),0.08)', background: 'rgba(var(--v4-shadow-rgb),calc(0.2*var(--v4-shadow-k)))', display: 'flex', justifyContent: 'center' }}>
        <ChartContainer
          series={[{ type: 'line', data: [] }]}
          xAxis={[{ data: [dayjs().startOf('day'), dayjs().endOf('day')], scaleType: 'band', id: 'x' }]}
          yAxis={[{ max: 35, min: 0, scaleType: 'linear', id: 'y' }]}
          width={500}
          height={180}
          sx={{
            '& .MuiChartsAxis-line, & .MuiChartsAxis-tick': { stroke: 'rgba(var(--v4-cyan-rgb),0.18)' },
            '& .MuiChartsAxis-tickLabel': { fill: 'rgba(var(--v4-muted-rgb),0.45)', fontSize: '0.68rem' },
            '& .MuiChartsAxis-label': { fill: 'rgba(var(--v4-muted-rgb),0.45)' },
            background: 'transparent',
          }}
        >
          <LinePlot />
          <ChartsXAxis position="bottom" axisId="x" />
          <ChartsYAxis label="°C" position="left" axisId="y" />
          <ChartsReferenceLine
            y={state.config.temperatur_tag}
            label="Tag"
            axisId="y"
            lineStyle={{ stroke: cyan, strokeWidth: 1.5, strokeDasharray: '4 3' }}
            labelStyle={{ fill: 'rgba(var(--v4-cyan-rgb),0.7)', fontSize: '0.7rem' }}
          />
          <ChartsReferenceLine
            y={state.config.temperatur_nacht}
            label="Nacht"
            axisId="y"
            lineStyle={{ stroke: 'rgba(var(--v4-purple-rgb),0.7)', strokeWidth: 1.5, strokeDasharray: '4 3' }}
            labelStyle={{ fill: 'rgba(var(--v4-purple-rgb),0.7)', fontSize: '0.7rem' }}
          />
        </ChartContainer>
      </Box>

      {!is_managed && (
        <Button
          fullWidth variant="contained" startIcon={<SaveRoundedIcon />}
          onClick={saveConfig}
          sx={{
            mt: 2.5, borderRadius: '12px', fontWeight: 700, py: 1.2,
            background: 'linear-gradient(135deg,var(--v4-cyan),var(--v4-cyan-deep))',
            boxShadow: '0 4px 18px rgba(var(--v4-cyan-rgb),0.28)',
            '&:hover': { boxShadow: '0 6px 24px rgba(var(--v4-cyan-rgb),0.4)' },
          }}
        >
          Speichern
        </Button>
      )}
    </Box>
  );
}
